import { convertEventParameterTypes } from "../utils/typeConverters.js";

/**
 * Normalisiert Event-Parameter zu GA4-konformen Attributen
 *
 * Korrigiert fehlerhafte/nicht-konforme Event-Attribute wie:
 * - totalvalue → value
 * - currencycode → currency
 * - id → transaction_id (bei purchase)
 * - component → item_list_id
 * - etc.
 *
 * Verwendung:
 * ```javascript
 * const normalizer = new EventAttributeNormalizer();
 * const normalizedParams = normalizer.normalizeParams('purchase', {
 *   revenue: '99.99',
 *   currencycode: 'EUR'
 * });
 * // { value: 99.99, currency: 'EUR' }
 * ```
 */
export default class EventAttributeNormalizer {
    /**
     * Normalisiert nur die Parameter eines Events (ohne Items)
     * Nützlich für direkte pushEvent() Aufrufe, die alte Attributnamen verwenden
     * @param {string} eventName - Name des Events
     * @param {Object} params - Event-Parameter
     * @returns {Object} Normalisierte Parameter
     */
    normalizeParams(eventName, params) {
        return this.#normalizeEventParams(eventName, params);
    }

    /**
     * Normalisiert Event-Level Parameter
     * @private
     */
    #normalizeEventParams(eventName, attributes) {
        // Start mit allen ursprünglichen Attributen (für Felder die nicht transformiert werden)
        const params = { ...attributes };

        // Event-spezifische Transformationen (überschreiben die Originalwerte)
        switch (eventName) {
            case 'add_to_cart':
            case 'remove_from_cart':
            case 'view_cart':
            case 'begin_checkout':
                // totalvalue → value
                if (attributes.totalvalue !== undefined) {
                    params.value = parseFloat(attributes.totalvalue);
                    delete params.totalvalue;
                }
                break;

            case 'purchase':
                // currencycode → currency
                if (attributes.currencycode) {
                    params.currency = attributes.currencycode;
                    delete params.currencycode;
                }
                // id → transaction_id
                if (attributes.id) {
                    params.transaction_id = attributes.id;
                    delete params.id;
                }
                // revenue → value
                if (attributes.revenue !== undefined) {
                    params.value = parseFloat(attributes.revenue);
                    delete params.revenue;
                }
                // shipping: String → Float
                if (params.shipping !== undefined) {
                    params.shipping = parseFloat(params.shipping);
                }
                break;

            case 'add_to_wishlist':
            case 'view_item':
                // value: String → Float
                if (params.value !== undefined) {
                    params.value = parseFloat(params.value);
                }
                break;

            case 'add_shipping_info':
            case 'add_payment_info':
                // value: String → Float
                if (params.value !== undefined) {
                    params.value = parseFloat(params.value);
                }
                break;

            case 'select_item':
            case 'view_item_list':
                // component → item_list_id
                if (attributes.component) {
                    params['item-list-id'] = attributes.component;
                    delete params.component;
                }
                // list → item_list_name
                if (attributes.list) {
                    params['item-list-name'] = attributes.list;
                    delete params.list;
                } else if (attributes.component) {
                    // Fallback: component als item_list_name
                    params['item-list-name'] = attributes.component;
                }
                // products wird separat als items behandelt, nicht in params
                if (eventName === 'view_item_list') {
                    delete params.products;
                }
                // Fallback: item_list_id als item_list_name wenn item_list_name fehlt
                if (!params.item_list_name && !params['item-list-name']) {
                    const listId = params.item_list_id || params['item-list-id'];
                    if (listId) {
                        params.item_list_name = listId;
                    }
                }
                break;

            case 'select_promotion':
            case 'view_promotion':
                // promotion_creative → creative_name
                if (attributes.promotion_creative) {
                    params.creative_name = attributes.promotion_creative;
                    delete params.promotion_creative;
                }
                // position → creative_slot (als String)
                if (attributes.position !== undefined) {
                    params.creative_slot = String(attributes.position);
                    delete params.position;
                }
                // promotion_id (Fallback: component)
                if (!attributes.promotion_id && attributes.component) {
                    params.promotion_id = attributes.component;
                }
                // component wird nicht im Event benötigt (entweder als promotion_id oder gar nicht)
                delete params.component;
                // products wird separat als items behandelt, nicht in params
                if (eventName === 'view_promotion') {
                    delete params.products;
                }
                break;
        }

        // Typ-Konvertierung für alle Parameter (unabhängig vom Event)
        return convertEventParameterTypes(params);
    }
}
